import { useState, useRef, useCallback } from 'react';

interface UIState {
  hoveredChunk: number | null;
  activeChunk: number | null;
  showToolbar: boolean;
  showButton: boolean;
  showAnalysis: boolean;
  analysisContent: string;
  isAnalyzing: boolean;
  isUserSeeking: boolean;
  selectedWord: string;
  showWordDrawer: boolean;
  lastTapTime: number;
  toolbarPosition: { x: number; y: number } | null;
}

export function useUIState() {
  // Consolidated UI state
  const [uiState, setUIState] = useState<UIState>({
    hoveredChunk: null,
    activeChunk: null,
    showToolbar: false,
    showButton: true,
    showAnalysis: false,
    analysisContent: '',
    isAnalyzing: false,
    isUserSeeking: false,
    selectedWord: '',
    showWordDrawer: false,
    lastTapTime: 0,
    toolbarPosition: null,
  });

  // Refs
  const tapTimerRef = useRef<number | null>(null);
  const userSeekingTimeoutRef = useRef<number | null>(null);

  // Optimized state updaters
  const updateUIState = useCallback((updates: Partial<UIState>) => {
    setUIState(prev => ({ ...prev, ...updates }));
  }, []);

  const setHoveredChunk = useCallback((chunk: number | null) => {
    updateUIState({ hoveredChunk: chunk });
  }, [updateUIState]);

  const setActiveChunk = useCallback((chunk: number | null) => {
    updateUIState({ activeChunk: chunk });
  }, [updateUIState]);

  const setShowToolbar = useCallback((show: boolean) => {
    updateUIState({ showToolbar: show });
  }, [updateUIState]);

  const setShowButton = useCallback((show: boolean) => {
    updateUIState({ showButton: show });
  }, [updateUIState]);

  const setShowAnalysis = useCallback((show: boolean) => {
    updateUIState({ showAnalysis: show });
  }, [updateUIState]);

  const setAnalysisContent = useCallback((content: string) => {
    updateUIState({ analysisContent: content });
  }, [updateUIState]);

  const setIsAnalyzing = useCallback((analyzing: boolean) => {
    updateUIState({ isAnalyzing: analyzing });
  }, [updateUIState]);

  const setIsUserSeeking = useCallback((seeking: boolean) => {
    updateUIState({ isUserSeeking: seeking });
  }, [updateUIState]);

  const setSelectedWord = useCallback((word: string) => {
    updateUIState({ selectedWord: word });
  }, [updateUIState]);

  const setShowWordDrawer = useCallback((show: boolean) => {
    updateUIState({ showWordDrawer: show });
  }, [updateUIState]);

  const setLastTapTime = useCallback((time: number) => {
    updateUIState({ lastTapTime: time });
  }, [updateUIState]);

  const setToolbarPosition = useCallback((position: { x: number; y: number } | null) => {
    updateUIState({ toolbarPosition: position });
  }, [updateUIState]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
    }
    if (userSeekingTimeoutRef.current !== null) {
      window.clearTimeout(userSeekingTimeoutRef.current);
      userSeekingTimeoutRef.current = null;
    }
  }, []);

  return {
    // State
    uiState,
    
    // Individual setters
    setHoveredChunk,
    setActiveChunk,
    setShowToolbar,
    setShowButton,
    setShowAnalysis,
    setAnalysisContent,
    setIsAnalyzing,
    setIsUserSeeking,
    setSelectedWord,
    setShowWordDrawer,
    setLastTapTime,
    setToolbarPosition,
    
    // Batch updater
    updateUIState,
    
    // Refs
    tapTimerRef,
    userSeekingTimeoutRef,
    
    // Cleanup
    cleanup,
  };
} 